import type { ThreadActionMenuState } from "./threadActionMenu.logic";
import { isDurablySettled } from "./threadSettled";
import type { SidebarThreadSummary, Thread } from "../../types";

export interface SidebarRuntimeActionInput {
  readonly sessionStatus: string | null | undefined;
  readonly activeTurnId: string | null | undefined;
  readonly hasRetryPrompt: boolean;
}

export interface SidebarRuntimeActions {
  readonly canStop: boolean;
  readonly canContinue: boolean;
  readonly canRetry: boolean;
  readonly canStartFreshSession: boolean;
}

function hasActiveTurnId(turnId: string | null | undefined): boolean {
  return typeof turnId === "string" && turnId.trim().length > 0;
}

export function resolveSidebarRuntimeActions(
  input: SidebarRuntimeActionInput,
): SidebarRuntimeActions {
  const isRunning =
    input.sessionStatus === "running" ||
    input.sessionStatus === "connecting" ||
    hasActiveTurnId(input.activeTurnId);

  if (isRunning) {
    return {
      canStop: true,
      canContinue: false,
      canRetry: false,
      canStartFreshSession: false,
    };
  }

  return {
    canStop: false,
    canContinue: true,
    canRetry: input.hasRetryPrompt,
    canStartFreshSession: input.sessionStatus === "error",
  };
}

export interface ThreadRowContextMenuInput {
  readonly thread: Pick<
    SidebarThreadSummary,
    | "environmentId"
    | "titleRegeneration"
    | "branch"
    | "archivedAt"
    | "archive"
    | "settledAt"
    | "settledOverride"
  >;
  readonly isPinned: boolean;
  readonly runtimeActions: SidebarRuntimeActions;
  readonly canStartNewThreadOnBranch: boolean;
  readonly canUnarchive: boolean;
  readonly canUnsettle: boolean;
}

/**
 * The last thing the user typed, for "Retry last prompt".
 *
 * Shared by both menu callers (row and breadcrumb) — it is a read over the
 * thread's messages and nothing else, so it does not belong to either one.
 */
export function resolveRetryPromptText(threadDetail: Thread | undefined | null): string | null {
  return (
    threadDetail?.messages
      .toReversed()
      .find(
        (message) =>
          message.role === "user" &&
          typeof message.text === "string" &&
          message.text.trim().length > 0,
      )?.text ?? null
  );
}

/** The row context menu's options, derived from a thread and this caller's capabilities. */
export function resolveThreadRowContextMenuOptions(
  input: ThreadRowContextMenuInput,
): ThreadActionMenuState {
  return {
    isPinned: input.isPinned,
    isRegenerating: input.thread.titleRegeneration != null,
    runtimeActions: input.runtimeActions,
    branch: input.thread.branch,
    canStartNewThreadOnBranch: input.canStartNewThreadOnBranch,
    isArchived: input.thread.archivedAt != null,
    archiveState: input.thread.archive?.state ?? null,
    canUnarchive: input.canUnarchive,
    isSettled: isDurablySettled(input.thread),
    canUnsettle: input.canUnsettle,
  };
}
